import axios from 'axios';
import apiClient from 'utils/apiClient';
import { getRequestConfig } from 'hooks/utils';

export interface HolidayInputs {
  holiday_date: string;
  code: string;
  country: string;
  states: string;
  school_ids: string;
  no_reflow_websites: string;
  no_reflow_school_ids: string;
  no_reflow: boolean;
}

export interface RunRow {
  school_id: string;
  state: string | null;
  action: string;
  displaced_code: string | null;
  reflow: boolean;
  reflow_reason: string | null;
  reflow_start: string | null;
  reflow_end: string | null;
  termination_reason: string | null;
  reflow_rows_changed: number;
}

export interface RunReport {
  run_id: string;
  environment: string;
  status: 'queued' | 'running' | 'done' | 'failed' | 'unknown';
  params?: {
    date: string;
    code: string;
    country: string;
    dry_run: boolean;
    states: string[] | null;
    school_ids: string[] | null;
    no_reflow: boolean;
    no_reflow_websites: string[];
    no_reflow_school_ids: string[];
  };
  summary?: {
    schools: number;
    close: number;
    reflows_queued: number;
    reflow_rows_in_preview: number;
    actions: Record<string, number>;
    reflow_reasons: Record<string, number>;
  };
  error?: string;
  matching_rows?: number;
  rows?: RunRow[];
}

export interface StartedRun {
  run_id: string;
  status: 'queued';
  environment: string;
  dry_run: boolean;
}

const base = 'v2/admin/holiday-closures';

export const holidayApi = {
  config: async () =>
    (
      await apiClient.get<{ environment: string; review_ttl_seconds: number }>(
        `${base}/config`,
        getRequestConfig(),
      )
    ).data,
  dryRun: async (inputs: HolidayInputs) =>
    (
      await apiClient.post<StartedRun>(
        `${base}/dry-run`,
        inputs,
        getRequestConfig(),
      )
    ).data,
  report: async (id: string, action: string, reflowOnly: boolean) =>
    (
      await apiClient.get<RunReport>(`${base}/runs/${encodeURIComponent(id)}`, {
        ...getRequestConfig(),
        params: { action, reflow_only: reflowOnly, limit: 3000 },
      })
    ).data,
  apply: async (
    inputs: HolidayInputs,
    dryRunId: string,
    environment: string,
    confirmation: string,
  ) =>
    (
      await apiClient.post<StartedRun>(
        `${base}/apply`,
        {
          ...inputs,
          dry_run_id: dryRunId,
          environment,
          confirmation,
        },
        getRequestConfig(),
      )
    ).data,
};

export function errorMessage(error: unknown): string {
  if (axios.isAxiosError<{ detail?: unknown }>(error)) {
    const detail = error.response?.data.detail;
    if (typeof detail === 'string') return detail;
    if (error.response?.status === 404)
      return 'Holiday closures is not available in this environment yet. The dashboard API update must be deployed first.';
    if (error.response?.status === 403)
      return 'School or schedule management access is required.';
    if (error.response?.status === 422)
      return 'Check the date, holiday code, country, and run ID.';
  }
  return error instanceof Error
    ? error.message
    : 'The request failed. Please try again.';
}
